import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/common';

// Validation schema
const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

function Login() {
  const { login, loading, error: authError } = useAuth();
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // If there's an error in the auth context, display it
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    
    try {
      // console.log('Attempting login with:', values.email);
      const result = await login(values.email, values.password);
      
      // console.log('Login.jsx: Received result from context:', result);

      // Check result status returned from AuthContext
      if (result && result.status === 'ACTIVE') {
        // Successful login for an active user
        // console.log('Login.jsx: Navigating to dashboard');
        navigate('/dashboard'); 
      } else if (result && result.status === 'PENDING') {
        // User authenticated but account is pending approval
        // console.log('Login.jsx: Navigating to pending approval');
        navigate('/pending-approval');
      } else {
        // Handle FAILED, BAD_REQUEST, SERVER_ERROR, or other non-successful statuses
        const errorMessage = result?.message || 'Login failed. Please check your credentials or try again later.';
        // console.log(`Login.jsx: Login failed with status: ${result?.status || 'UNKNOWN'}, message: ${errorMessage}`);
        setError(errorMessage);
        // Scroll to top to ensure error message is visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      // This catch block now primarily handles unexpected errors *within* AuthContext 
      // or if AuthContext itself throws an error that isn't caught internally.
      // Most API errors (4xx, 5xx) should be caught within AuthContext and returned 
      // as a result object { success: false, status: ..., message: ... }
      // console.error('Login.jsx: handleSubmit error caught:', err);
      
      // Display a generic error message, as specific messages should be in the result object
      const errorMessage = err?.message || 'An unexpected error occurred during login.';
      setError(errorMessage); 
      
      // Scroll to top of page to ensure error message is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold text-center mb-4 text-hooray-red font-mont">Sign in to your account</h1>
      
      {/* Error message display */}
      {error && (
        <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md" 
             role="alert"
             style={{ whiteSpace: 'pre-line' }}>
          <p className="font-bold">Login Error</p>
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-6">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Field
                  type="password"
                  name="password"
                  id="password"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <Button
                type="submit"
                variant="cta"
                disabled={isSubmitting || loading}
                className="w-full"
              >
                {isSubmitting || loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Logging in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>
            </Form>
          )}
        </Formik>
        
        <div className="mt-6 text-center">
          <Link 
            to="/forgot-password" 
            className="text-sm text-hooray-red hover:text-hooray-yellow font-medium mb-4 block"
          >
            Forgot your password?
          </Link>
          
          <p className="mt-6 text-sm text-center text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-hooray-red hover:text-hooray-yellow font-medium">
              Register here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login; 